import { Box, Paper, Typography, Stack, alpha, Chip } from "@mui/material";

interface MetricItemProps {
  label: string;
  value: string | number;
  unit?: string;
  accent?: string;
}

const MetricItem = ({ label, value, unit, accent }: MetricItemProps) => (
  <Box sx={{ py: 0.75 }}>
    <Typography
      variant="overline"
      color="text.secondary"
      sx={{ display: "block", lineHeight: 1.5 }}
    >
      {label}
    </Typography>
    <Stack direction="row" alignItems="baseline" spacing={0.5}>
      <Typography
        variant="h6"
        fontWeight={600}
        color={accent || "text.primary"}
      >
        {value}
      </Typography>
      {unit && (
        <Typography variant="caption" color="text.secondary">
          {unit}
        </Typography>
      )}
    </Stack>
  </Box>
);

export const StatsPanel = () => {
  const stats = {
    totalNodes: 13000,
    onlineNodes: 12847,
    pipelineKm: 7600,
  };

  const uptimePercent = ((stats.onlineNodes / stats.totalNodes) * 100).toFixed(1);

  return (
    <Box
      sx={{
        position: "absolute",
        top: 16,
        left: 16,
        zIndex: 10,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      {/* Main Stats Card */}
      <Paper
        elevation={0}
        sx={{
          width: 200,
          bgcolor: (theme) => alpha(theme.palette.background.paper, 0.85),
          backdropFilter: "blur(12px)",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        {/* Header with uptime indicator */}
        <Box
          sx={{
            px: 2,
            py: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: "success.main",
                animation: "pulse 2s infinite",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1, transform: "scale(1)" },
                  "50%": { opacity: 0.6, transform: "scale(0.9)" },
                },
              }}
            />
            <Typography variant="caption" fontWeight={600}>
              Network
            </Typography>
          </Stack>
          <Chip
            label={`${uptimePercent}% up`}
            color="success"
            size="small"
            sx={{ height: 20 }}
          />
        </Box>

        {/* Metrics */}
        <Box sx={{ px: 2, py: 0.5 }}>
          <MetricItem
            label="Active Nodes"
            value={stats.onlineNodes.toLocaleString()}
            unit={`/ ${(stats.totalNodes / 1000).toFixed(0)}k`}
          />
          <Box sx={{ borderTop: "1px solid", borderColor: "divider" }} />
          <MetricItem
            label="Pipeline"
            value={(stats.pipelineKm / 1000).toFixed(1)}
            unit="k km"
          />
        </Box>
      </Paper>

      {/* System Status */}
      <Chip
        icon={
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: "success.main",
              ml: 1,
            }}
          />
        }
        label="All Systems Operational"
        color="success"
        variant="outlined"
        size="small"
      />
    </Box>
  );
};
